import { AUTH_KEYS } from "./authKeys";

export type ParsedLogoutExpiry =
  | { type: "missing" }
  | { type: "invalid" }
  | { type: "ok"; expiresAtMs: number };

export function parseLogoutExpiryFromStorage(): ParsedLogoutExpiry {
  const raw = localStorage.getItem(AUTH_KEYS.LOGOUT_TIMEOUT);
  if (raw == null) return { type: "missing" };
  const trimmed = raw.trim();
  if (trimmed === "") return { type: "missing" };
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return { type: "invalid" };
  return { type: "ok", expiresAtMs: n };
}

export function hasStoredAuthSession(): boolean {
  const storedAuth = localStorage.getItem(AUTH_KEYS.AUTHORIZATION);
  const storedForce = localStorage.getItem(AUTH_KEYS.AUTHORIZATION_FORCE);
  const storedRA = localStorage.getItem(AUTH_KEYS.AUTHORIZATION_RA);
  const storedUser = localStorage.getItem(AUTH_KEYS.SESSION);
  const storedInForce = localStorage.getItem(AUTH_KEYS.IN_FORCE) === "true";
  const hasAuth = !!(storedAuth || storedForce);
  const hasRA = storedInForce ? true : !!storedRA;
  return hasAuth && hasRA && !!storedUser;
}

export function getDefaultSessionMs(inForce: boolean): number {
  return inForce ? 1 * 60 * 60 * 1000 : 5 * 60 * 60 * 1000;
}

/** Renovar sessão quando faltar esse tempo (ou já expirou). */
export const SESSION_REFRESH_BEFORE_MS = 5 * 60 * 1000;

export function isForceSession(): boolean {
  return localStorage.getItem(AUTH_KEYS.IN_FORCE) === "true";
}

export function shouldAttemptSessionRefresh(now = Date.now()): boolean {
  if (!hasStoredAuthSession()) return false;
  if (!isForceSession()) return false;

  const exp = parseLogoutExpiryFromStorage();
  if (exp.type === "ok") {
    const remaining = exp.expiresAtMs - now;
    return remaining <= SESSION_REFRESH_BEFORE_MS;
  }

  return isSessionExpired(now);
}

/** Tempo restante em ms; 0 se expirado; null se não há sessão configurada. */
export function getRemainingSessionMs(): number | null {
  const exp = parseLogoutExpiryFromStorage();
  if (exp.type !== "ok") return null;
  return Math.max(0, exp.expiresAtMs - Date.now());
}

export function isSessionExpired(now = Date.now()): boolean {
  const exp = parseLogoutExpiryFromStorage();

  if (exp.type === "invalid") return true;
  if (exp.type === "ok") return now >= exp.expiresAtMs;

  if (!hasStoredAuthSession()) return false;

  const storedUser = localStorage.getItem(AUTH_KEYS.SESSION);
  if (!storedUser) return true;

  try {
    const user = JSON.parse(storedUser) as { loginTimestamp?: number };
    const loginTs = user.loginTimestamp;
    if (typeof loginTs !== "number" || !Number.isFinite(loginTs)) return true;

    const inForce = localStorage.getItem(AUTH_KEYS.IN_FORCE) === "true";
    return now >= loginTs + getDefaultSessionMs(inForce);
  } catch {
    return true;
  }
}

export function writeLogoutExpiry(timeoutMs: number): void {
  localStorage.setItem(AUTH_KEYS.LOGOUT_TIMEOUT, String(Date.now() + timeoutMs));
}
